import { useEffect, useState } from "react";
import useInfrastructure from "hooks/useInfrastructure.hook";
import ChatService from "services/chat.service";

const MEDIA_REQUEST_RETRIES = 3;
const MEDIA_REQUEST_RETRY_DELAY_MS = 350;

function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function runRequestWithRetry({ requestFn, isCancelled }) {
  let lastResponse = null;

  for (let attempt = 1; attempt <= MEDIA_REQUEST_RETRIES; attempt += 1) {
    if (isCancelled()) return null;

    try {
      const response = await requestFn();
      if (response?.success) {
        return response;
      }
      lastResponse = response;
    } catch (error) {
      lastResponse = { success: false, error };
    }

    if (attempt < MEDIA_REQUEST_RETRIES) {
      await wait(MEDIA_REQUEST_RETRY_DELAY_MS * attempt);
    }
  }

  return lastResponse;
}

export function useChatEventFile(event) {
  const { repositories } = useInfrastructure();
  const filesRepository = repositories.files;
  const [url, setUrl] = useState(null);
  const cacheEntityId = event.id;
  const lineId = event.lineId;
  const mediaKey = event?.type ? event.type.toLowerCase() : null;
  const mediaData = mediaKey ? event?.[mediaKey] : null;
  const mediaId = mediaData?.id ?? null;
  const mediaUrl = mediaData?.url ?? null;

  useEffect(() => {
    setUrl(null);
    if (!cacheEntityId) return;
    if (mediaId && !lineId) return;
    if (!mediaId && !mediaUrl) return;

    let cancelled = false;
    let objectUrl = null;

    async function loadFile() {
      try {
        let file = await filesRepository.getByEntity("CHAT", "EVENT", cacheEntityId);
        let blob;

        if (file && file?.blob) {
          blob = file.blob;
        } else {
          let mediaResponse = null;

          if (mediaId) {
            mediaResponse = await runRequestWithRetry({
              requestFn: () => ChatService.getMedia({ lineId, mediaId }),
              isCancelled: () => cancelled,
            });
          } else if (mediaUrl) {
            mediaResponse = await runRequestWithRetry({
              requestFn: async () => {
                const response = await fetch(mediaUrl);
                if (!response.ok) {
                  throw new Error(`Request failed with status ${response.status}`);
                }

                const blob = await response.blob();
                if (!(blob instanceof Blob)) {
                  throw new Error("Invalid blob response");
                }

                return { success: true, payload: blob };
              },
              isCancelled: () => cancelled,
            });
          }

          if (cancelled) return;

          const { success, payload } = mediaResponse ?? {};
          if (success && payload) {
            blob = payload;
            if (!blob || !(blob instanceof Blob)) return;
  
            const fileId = `chat:event:${cacheEntityId}:file`;
            await filesRepository.upsert({
              id: fileId,
              scope: "CHAT",
              entity: "EVENT",
              entityId: cacheEntityId,
              blob,
              mimeType: blob.type
            });
          } else {
            console.warn("useChatEventFile: media request failed after retries", {
              lineId,
              mediaId,
              mediaUrl,
              error: mediaResponse?.error
            });
          }
        }

        if (cancelled) return;

        if (blob) {
          objectUrl = (window.URL || window.webkitURL).createObjectURL(blob);
          setUrl(objectUrl);
        }
      } catch (err) {
        console.error("useChatEventFile error:", err);
      }
    }

    loadFile();

    return () => {
      cancelled = true;
      if (objectUrl) {
        (window.URL || window.webkitURL).revokeObjectURL(objectUrl);
      }
    };
  }, [lineId, mediaId, mediaUrl, cacheEntityId, filesRepository]);

  return [url, cacheEntityId];
}
